/**
 * txSummary — best-effort, one-line human-readable summary of a transaction.
 *
 * Cherry-pick #1 of the /tx detail enrichment. Derives a single plain-English
 * sentence from the transaction data the TransactionDetails page ALREADY has
 * (instructions, pre/postTokenBalances, native pre/postBalances, meta) — no
 * extra network calls. The detector is intentionally best-effort: it recognises
 * the common shapes (swap, SOL/SPL transfer, wrap/unwrap native, stake, single
 * program interaction) and otherwise falls back to a generic
 * "N instructions across M programs" line so it NEVER renders empty.
 *
 * Examples:
 *   "Swapped 27.49 USDC for 0.366 XNT on XDEX"
 *   "Traded −5 A → +3 B, +1 C on XDEX"          (multi-leg route)
 *   "Added liquidity 5 A + 200 B on XDEX"        (LP lifecycle via logs)
 *   "Sent 0.5 XNT to Bc9v…R6Vv" / "Received 120 USDC from 7dHb…9kQp"
 *   "Sent an NFT to 7dHb…9kQp"                   (0 decimals, qty 1)
 *   "Approved 500 USDC for 7dHb…9kQp"            (parsed token approve)
 *   "Delegated stake to validator Bc9v…R6Vv"     (parsed stake ix)
 *   "Wrapped 1.2 XNT"
 *   "Interacted with XDEX (3 instructions)"
 *   "4 instructions across 3 programs"
 */

import {
  Repeat2,
  Send,
  Coins,
  Lock,
  Layers,
  type LucideIcon,
} from 'lucide-react'
import { getProgramMeta } from './programRegistry'

/** Wrapped-native mint — represents XNT held inside an SPL token account. */
const WRAPPED_NATIVE_MINT = 'So11111111111111111111111111111111111111112'
const SYSTEM_PROGRAM = '11111111111111111111111111111111'
const COMPUTE_BUDGET = 'ComputeBudget111111111111111111111111111111'
const STAKE_PROGRAM = 'Stake11111111111111111111111111111111111111'

/** Program ids whose presence names a swap venue. */
const DEX_PROGRAM_IDS: Record<string, string> = {
  sEsYH97wqmfnkzHedjNcw3zyJdPvUmsa9AixhS4b4fN: 'XDEX',
  xdevKAYaAZFhACeWjNj7LQKy6dcWsTXmk7pz79qNhe6: 'XDEX',
  '675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8': 'Raydium',
  RVKd61ztZW9GUwhRbbLoYVRE5Xf1B2tVscKqwZqXgEr: 'Raydium',
  whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc: 'Orca',
  '9xQeWvG816bUx9EPjHmaT23yvVM2ZWbrrpZb9PusVFin': 'Serum',
}

/** Minimal well-known mint → symbol map (client-side, no fetch). */
const KNOWN_MINT_SYMBOLS: Record<string, string> = {
  [WRAPPED_NATIVE_MINT]: 'XNT',
  EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: 'USDC',
  Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB: 'USDT',
}

export interface TxSummaryInput {
  instructions?: any[]
  preTokenBalances?: any[]
  postTokenBalances?: any[]
  preBalances?: number[]
  postBalances?: number[]
  accountKeys?: string[]
  fromAddress?: string
  toAddress?: string
  fee?: number
  /** Program log messages — used to identify XDEX LP lifecycle instructions. */
  logMessages?: string[]
}

/** One side of a recognised swap — enough identity for the page to render icon + name. */
export interface TxSummaryAsset {
  /** Mint address, or the 'native' sentinel for un-wrapped XNT. */
  mint: string
  /** Best-effort display symbol (short mint until metadata resolves). */
  symbol: string
  amount: number
}

export interface TxSummary {
  text: string
  Icon: LucideIcon
  /** Present when the tx is a recognised 1:1 swap; the page renders a rich icon/name line from it. */
  swap?: { sent: TxSummaryAsset; received: TxSummaryAsset; venue: string }
  /**
   * Present for multi-leg DEX activity that isn't a clean 1:1 swap — multi-hop
   * routes and multi-asset trades. Either side may be empty (e.g. a route
   * where the fee payer only nets inflows).
   */
  trade?: {
    sent: TxSummaryAsset[]
    received: TxSummaryAsset[]
    venue: string
  }
  /** Present for XDEX LP lifecycle txs (add/remove liquidity, pool create, fee claim). */
  lp?: { label: string; assets: TxSummaryAsset[]; venue: string }
  /** Present for single-asset transfers (native XNT, SPL tokens, NFTs). */
  transfer?: {
    asset: TxSummaryAsset
    /** The other party (recipient when sent, sender when received), if known. */
    counterparty?: string
    direction: 'sent' | 'received'
    /** True when the asset moved like an NFT (0 decimals, quantity 1). */
    isNft?: boolean
  }
  /** Present for stake delegations — lets the page resolve the validator's display name. */
  stake?: { action: 'delegate'; voteAccount: string }
}

/** Compact address label, e.g. `Bc9v…R6Vv`. */
function shortAddr(addr?: string): string {
  if (!addr) return 'unknown'
  if (addr.length <= 12) return addr
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`
}

/** Trim a numeric amount to at most 6 significant fractional digits, no trailing zeros. */
export function formatAmount(n: number): string {
  if (!isFinite(n)) return '0'
  const abs = Math.abs(n)
  const decimals = abs >= 1 ? 4 : 6
  const s = n.toFixed(decimals)
  return s.replace(/\.?0+$/, '') || '0'
}

/**
 * Compact, human-readable amount for summary lines: grouped with 2 decimals
 * below a million ("456,141.06"), then 2-decimal M/B/T/Q suffixes above
 * ("45.64M", "1.2T"). Sub-1 amounts keep extra precision so dust doesn't
 * collapse to "0.00".
 */
export function formatCompactAmount(n: number): string {
  if (!isFinite(n) || n === 0) return '0'
  const abs = Math.abs(n)
  if (abs < 1) return formatAmount(n)
  const units: [number, string][] = [
    [1e15, 'Q'],
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
  ]
  for (const [div, suffix] of units) {
    if (abs >= div) {
      return `${(n / div).toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`
    }
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

/**
 * Resolve a mint to a display symbol. Prefers an enriched symbol (e.g. from a
 * metadata query), then the well-known map, then a truncated mint.
 */
export function resolveMintSymbol(
  mint: string | undefined,
  enriched?: Record<string, string>,
): string {
  if (!mint) return 'tokens'
  // Wrapped-native (So111…112) is wrapped XNT on X1, NOT SOL. The community
  // token list (via `enriched`) mislabels it 'SOL', which flips a wrap into a
  // bogus "Swap XNT → SOL" once metadata loads. Pin it to the canonical symbol
  // ahead of the enriched map so native and wrapped compare equal.
  if (mint === WRAPPED_NATIVE_MINT) return KNOWN_MINT_SYMBOLS[mint]
  return enriched?.[mint] || KNOWN_MINT_SYMBOLS[mint] || shortAddr(mint)
}

/**
 * Resolve the on-chain program id for one instruction. Compiled/v0 instructions
 * only carry a `programIdIndex` into the account-keys array; parsed/legacy ones
 * carry `programId` directly. Returns '' when unresolvable.
 */
export function resolveInstructionProgramId(
  instruction: any,
  accountKeys?: string[],
): string {
  const direct = instruction?.programId
  if (typeof direct === 'string') return direct
  if (direct && typeof direct.toString === 'function') {
    const s = direct.toString()
    if (s && s !== '[object Object]') return s
  }
  const idx = instruction?.programIdIndex
  if (typeof idx === 'number' && accountKeys && accountKeys[idx]) {
    return accountKeys[idx]
  }
  return ''
}

interface TokenDelta {
  owner?: string
  mint: string
  change: number
  decimals?: number
}

/** Net per-(accountIndex) token balance change, keyed by owner + mint. */
function computeTokenDeltas(tx: TxSummaryInput): TokenDelta[] {
  const pre = tx.preTokenBalances || []
  const post = tx.postTokenBalances || []
  const byIndex = new Map<number, TokenDelta>()

  const read = (b: any): number =>
    parseFloat(b?.uiTokenAmount?.uiAmountString ?? b?.uiTokenAmount?.uiAmount ?? '0') || 0

  for (const b of pre) {
    byIndex.set(b.accountIndex, {
      owner: b.owner,
      mint: b.mint,
      change: -read(b),
      decimals: b?.uiTokenAmount?.decimals,
    })
  }
  for (const b of post) {
    const existing = byIndex.get(b.accountIndex)
    if (existing) {
      existing.change += read(b)
      existing.owner = existing.owner ?? b.owner
      existing.decimals = existing.decimals ?? b?.uiTokenAmount?.decimals
    } else {
      byIndex.set(b.accountIndex, {
        owner: b.owner,
        mint: b.mint,
        change: read(b),
        decimals: b?.uiTokenAmount?.decimals,
      })
    }
  }
  return [...byIndex.values()].filter((d) => Math.abs(d.change) > 1e-12)
}

/** Distinct on-chain program ids referenced by top-level instructions. */
function programIds(tx: TxSummaryInput): string[] {
  const ids = (tx.instructions || [])
    .map((ix) => resolveInstructionProgramId(ix, tx.accountKeys))
    .filter(Boolean)
  return [...new Set(ids)]
}

/** Name the swap venue from the instruction program set (falls back to "DEX"). */
function dexName(ids: string[]): string {
  for (const id of ids) {
    if (DEX_PROGRAM_IDS[id]) return DEX_PROGRAM_IDS[id]
  }
  return 'DEX'
}

/**
 * Build the one-line summary. `opts.tokenSymbols` optionally supplies richer
 * mint→symbol resolution (e.g. from a metadata query); everything works without it.
 */
export function buildTxSummary(
  tx: TxSummaryInput,
  opts?: { tokenSymbols?: Record<string, string> },
): TxSummary {
  const enriched = opts?.tokenSymbols
  const ids = programIds(tx)
  const nonNativeIds = ids.filter(
    (id) => id !== SYSTEM_PROGRAM && id !== COMPUTE_BUDGET,
  )
  const deltas = computeTokenDeltas(tx)
  const feePayer = tx.fromAddress

  // Native (XNT) balance changes, fee removed from the fee payer's side.
  const keys = tx.accountKeys || []
  const pre = tx.preBalances || []
  const post = tx.postBalances || []
  const nativeChanges = keys.map((k, i) => {
    let change = (post[i] || 0) - (pre[i] || 0)
    if (i === 0) change += tx.fee || 0 // undo the fee the fee payer paid
    return { address: k, change: change / 1e9 }
  })

  // Token deltas owned by the fee payer, keyed by mint.
  const feePayerTokenByMint = new Map<string, number>()
  for (const d of deltas) {
    if (!feePayer || d.owner === feePayer) {
      feePayerTokenByMint.set(d.mint, (feePayerTokenByMint.get(d.mint) || 0) + d.change)
    }
  }
  const feePayerNative = nativeChanges[0]?.change ?? 0

  // Build (asset, change) pairs across SPL mints + native XNT.
  const swapAssets: { sym: string; mint: string; change: number }[] = []
  for (const [mint, change] of feePayerTokenByMint) {
    swapAssets.push({ sym: resolveMintSymbol(mint, enriched), mint, change })
  }
  // Native side counts only when it moved beyond dust (ignore pure fee noise).
  if (Math.abs(feePayerNative) > 1e-6) {
    swapAssets.push({ sym: 'XNT', mint: 'native', change: feePayerNative })
  }
  const sent = swapAssets.filter((a) => a.change < 0).sort((a, b) => a.change - b.change)
  const received = swapAssets.filter((a) => a.change > 0).sort((a, b) => b.change - a.change)
  const toAsset = (a: { sym: string; mint: string; change: number }): TxSummaryAsset => ({
    mint: a.mint,
    symbol: a.sym,
    amount: Math.abs(a.change),
  })
  const hasDex = ids.some((id) => DEX_PROGRAM_IDS[id])

  // ---- 0. XDEX LP lifecycle: add/remove liquidity, pool create, fee claim. ----
  // Identified from the Anchor instruction names in the program logs (the same
  // instruction set the xdex_lp_events pipeline decodes server-side). Must run
  // before the swap rules — a deposit (2 assets out, LP token in) would
  // otherwise read as a multi-leg trade.
  if (hasDex && tx.logMessages?.length) {
    const logs = tx.logMessages.join('\n')
    const lpAction = /Instruction: Deposit\b/.test(logs)
      ? { label: 'Added liquidity', side: sent }
      : /Instruction: Withdraw\b/.test(logs)
        ? { label: 'Removed liquidity', side: received }
        : /Instruction: Initialize\b/.test(logs)
          ? { label: 'Created a pool with', side: sent }
          : /Instruction: (CollectProtocolFee|CollectFundFee)\b/.test(logs)
            ? { label: 'Claimed fees', side: received }
            : null
    if (lpAction) {
      const venue = dexName(ids)
      const assets = lpAction.side.map(toAsset)
      const list = assets
        .map((a) => `${formatCompactAmount(a.amount)} ${a.symbol}`)
        .join(' + ')
      return {
        text: assets.length
          ? `${lpAction.label} ${list} on ${venue}`
          : `${lpAction.label} on ${venue}`,
        Icon: Repeat2,
        lp: { label: lpAction.label, assets, venue },
      }
    }
  }

  // ---- 1. SWAP: fee payer sends one asset, receives a different one. ----
  // Genuine swap = exactly one asset out, one asset in. Multi-asset moves
  // (add-liquidity, multi-hop) fall through to the accurate generic summary
  // rather than being mislabeled a "Swap".
  if (sent.length === 1 && received.length === 1 && sent[0].sym !== received[0].sym) {
    const inA = sent[0]
    const outA = received[0]
    const venue = dexName(ids)
    return {
      text: `Swapped ${formatCompactAmount(Math.abs(inA.change))} ${inA.sym} for ${formatCompactAmount(outA.change)} ${outA.sym} on ${venue}`,
      Icon: Repeat2,
      swap: {
        sent: { mint: inA.mint, symbol: inA.sym, amount: Math.abs(inA.change) },
        received: { mint: outA.mint, symbol: outA.sym, amount: outA.change },
        venue,
      },
    }
  }

  // ---- 1b. Multi-leg DEX trade: multi-hop routes and multi-asset moves,
  // including txs where the fee payer nets only inflows or only outflows.
  // These used to fall through and get mislabeled by the wrap rule below.
  if (hasDex && swapAssets.length >= 2 && !(sent.length === 1 && received.length === 1)) {
    const venue = dexName(ids)
    const side = (arr: typeof swapAssets, sign: string) =>
      arr.map((a) => `${sign}${formatCompactAmount(Math.abs(a.change))} ${a.sym}`).join(', ')
    const parts = [side(sent, '−'), side(received, '+')].filter(Boolean)
    return {
      text: `Traded ${parts.join(' → ')} on ${venue}`,
      Icon: Repeat2,
      trade: { sent: sent.map(toAsset), received: received.map(toAsset), venue },
    }
  }

  // ---- 2. WRAP / UNWRAP native (XNT <-> wrapped-native SPL) — STRICT. ----
  // A genuine wrap moves native XNT into wXNT (or back) for the fee payer and
  // does nothing else: no DEX involved, the wrapped mint is the fee payer's
  // ONLY token delta, and the native leg mirrors it (opposite sign, ~equal
  // magnitude modulo ATA rent). The old rule fired on ANY wXNT delta, which
  // mislabeled arbitrage profit legs as "Wrapped" and incoming wXNT transfers
  // as wraps.
  const wrappedChange = feePayerTokenByMint.get(WRAPPED_NATIVE_MINT)
  if (
    wrappedChange !== undefined &&
    Math.abs(wrappedChange) > 1e-9 &&
    !hasDex &&
    feePayerTokenByMint.size === 1 &&
    feePayerNative * wrappedChange < 0 &&
    Math.abs(feePayerNative + wrappedChange) <= Math.max(0.01, 0.05 * Math.abs(wrappedChange))
  ) {
    if (wrappedChange > 0) {
      return { text: `Wrapped ${formatCompactAmount(wrappedChange)} XNT`, Icon: Coins }
    }
    return { text: `Unwrapped ${formatCompactAmount(Math.abs(wrappedChange))} XNT`, Icon: Coins }
  }

  // ---- 3. SPL transfer (single mint decreased on one side, increased on another). ----
  const byMint = new Map<string, TokenDelta[]>()
  for (const d of deltas) {
    if (!byMint.has(d.mint)) byMint.set(d.mint, [])
    byMint.get(d.mint)!.push(d)
  }
  if (byMint.size === 1) {
    const [mint, ds] = [...byMint.entries()][0]
    const outSide = ds.find((d) => d.change < 0)
    const inSide = ds.find((d) => d.change > 0)
    if (outSide && inSide) {
      const amount = inSide.change
      const symbol = resolveMintSymbol(mint, enriched)
      const isNft = inSide.decimals === 0 && Math.abs(amount - 1) < 1e-9
      // Direction is relative to the fee payer: they sent unless they own the
      // receiving side and not the sending one.
      const feePayerReceived =
        !!feePayer && inSide.owner === feePayer && outSide.owner !== feePayer
      const direction: 'sent' | 'received' = feePayerReceived ? 'received' : 'sent'
      const counterparty = feePayerReceived ? outSide.owner : inSide.owner
      const what = isNft ? 'an NFT' : `${formatCompactAmount(amount)} ${symbol}`
      return {
        text: feePayerReceived
          ? `Received ${what} from ${shortAddr(counterparty)}`
          : `Sent ${what} to ${shortAddr(counterparty)}`,
        Icon: Send,
        transfer: {
          asset: { mint, symbol, amount },
          counterparty,
          direction,
          isNft,
        },
      }
    }
  }

  // ---- 4. Native XNT transfer (System program, single recipient gains). ----
  if (ids.includes(SYSTEM_PROGRAM) && deltas.length === 0) {
    const gainers = nativeChanges
      .slice(1)
      .filter((c) => c.change > 1e-9)
      .sort((a, b) => b.change - a.change)
    if (gainers.length && feePayerNative < 0) {
      const amount = gainers[0].change
      return {
        text: `Sent ${formatCompactAmount(amount)} XNT to ${shortAddr(gainers[0].address)}`,
        Icon: Send,
        transfer: {
          asset: { mint: 'native', symbol: 'XNT', amount },
          counterparty: gainers[0].address,
          direction: 'sent',
        },
      }
    }
  }

  // ---- 4b. Token approval / revoke (no balance deltas; parsed token ix). ----
  const TOKEN_PROGRAMS = new Set([
    'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
    'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',
  ])
  for (const ix of tx.instructions || []) {
    const parsedType = ix?.parsed?.type
    if (!parsedType) continue
    if (!TOKEN_PROGRAMS.has(resolveInstructionProgramId(ix, tx.accountKeys))) continue
    if (parsedType === 'approve' || parsedType === 'approveChecked') {
      const info = ix.parsed?.info || {}
      const sym = info.mint ? resolveMintSymbol(info.mint, enriched) : 'token'
      const amt = parseFloat(info.tokenAmount?.uiAmountString ?? '')
      const amountLabel = isFinite(amt) ? `${formatCompactAmount(amt)} ${sym}` : `${sym} spending`
      return {
        text: `Approved ${amountLabel} for ${shortAddr(info.delegate)}`,
        Icon: Lock,
      }
    }
    if (parsedType === 'revoke') {
      return { text: 'Revoked a token approval', Icon: Lock }
    }
  }

  // ---- 5. Stake — name the specific action when parsed instructions say so. ----
  if (ids.includes(STAKE_PROGRAM)) {
    const stakeTypes = (tx.instructions || [])
      .filter((ix) => resolveInstructionProgramId(ix, tx.accountKeys) === STAKE_PROGRAM)
      .map((ix) => ix?.parsed)
      .filter(Boolean)
    const byType = (t: string) => stakeTypes.find((p: any) => p.type === t)
    const delegate = byType('delegate')
    if (delegate) {
      const voteAccount = delegate.info?.voteAccount
      return {
        text: `Delegated stake to validator ${shortAddr(voteAccount)}`,
        Icon: Lock,
        stake: voteAccount ? { action: 'delegate', voteAccount } : undefined,
      }
    }
    if (byType('deactivate')) {
      return { text: 'Deactivated stake', Icon: Lock }
    }
    const withdraw = byType('withdraw')
    if (withdraw) {
      const lamports = Number(withdraw.info?.lamports || 0)
      return {
        text: lamports > 0
          ? `Withdrew ${formatCompactAmount(lamports / 1e9)} XNT from a stake account`
          : 'Withdrew from a stake account',
        Icon: Lock,
      }
    }
    if (byType('split')) return { text: 'Split a stake account', Icon: Lock }
    if (byType('merge')) return { text: 'Merged stake accounts', Icon: Lock }
    if (byType('initialize')) return { text: 'Created a stake account', Icon: Lock }
    return { text: `Stake transaction (${nonNativeIds.length || 1} program interaction)`, Icon: Lock }
  }

  // ---- 6. Single dominant program interaction. ----
  if (nonNativeIds.length === 1) {
    const name = getProgramMeta(nonNativeIds[0]).name
    const n = tx.instructions?.length || 1
    return {
      text: `Interacted with ${name} (${n} instruction${n === 1 ? '' : 's'})`,
      Icon: Layers,
    }
  }

  // ---- 7. Generic fallback. ----
  const ixCount = tx.instructions?.length || 0
  const progCount = ids.length || 1
  return {
    text: `${ixCount} instruction${ixCount === 1 ? '' : 's'} across ${progCount} program${progCount === 1 ? '' : 's'}`,
    Icon: Layers,
  }
}
